import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  WalletBillingCategoryEnum,
  WalletService,
  WalletSummary,
  WalletTypeEnum,
} from 'lib-trend-core';
import { catchError, of, takeUntil } from 'rxjs';

/**
 * Classificação de cobrança do disparo RCS, lida da carteira RCS da empresa.
 *
 * Mostra VALORES, não nomes de categoria: a tarifa SINGLE — a mesma que o
 * resumo usa como base do custo estimado — e a transição para a tarifa
 * CONVERSATIONAL, que substitui a avulsa quando o cliente responde
 * (SPEC-2026-08-typed-wallets RF-50, RF-52). Os dois números saem do mesmo
 * `priceTable` preenchido no painel admin que o formulário consulta, então o
 * bloco e o custo estimado nunca podem discordar.
 */
@Component({
    selector: 'campaign-rcs-billing',
    templateUrl: './campaign-rcs-billing.v2.component.html',
    styleUrls: ['./campaign-rcs-billing.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CampaignRcsBillingComponent extends AbstractComponent implements OnInit {

  /** Tarifa SINGLE: o que cada envio RCS custa na saída. Null = não cadastrada. */
  pricePerMessage: number | null = null;

  /** Tarifa CONVERSATIONAL: passa a valer, no lugar da SINGLE, se o cliente responder. */
  sessionPrice: number | null = null;

  billingOpen = true;

  constructor(
    injector: Injector,
    private walletService: WalletService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadRcsPriceTable();
  }

  private loadRcsPriceTable(): void {
    this.walletService.getSummary()
      .pipe(takeUntil(this.destroy$), catchError(() => of<Array<WalletSummary>>([])))
      .subscribe((wallets) => {
        const priceTable = (wallets ?? [])
          .find((wallet) => wallet.type === WalletTypeEnum.RCS)?.priceTable ?? null;

        this.pricePerMessage = this.priceOf(priceTable, WalletBillingCategoryEnum.SINGLE);
        this.sessionPrice = this.priceOf(priceTable, WalletBillingCategoryEnum.CONVERSATIONAL);
      });
  }

  /**
   * Uma tarifa ausente é null, nunca zero: zero seria um preço cadastrado, e o
   * template mostra travessão para o que não foi configurado.
   */
  private priceOf(priceTable: Record<string, number> | null, category: WalletBillingCategoryEnum): number | null {
    const price = priceTable?.[category];
    return typeof price === 'number' && Number.isFinite(price) ? price : null;
  }

}
